import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import PageTop from '../../components/simple/PageTop'
import LatestArticles from '../../components/complex/LatestArticles'
import Pagination from '../../components/complex/Pagination'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { Context } from '../..'
import useFetchItems from '../../hooks/useFetchItems'
import AppServices from '../../services/general/appServices'
import RegionServices from '../../services/general/regionServices'
import { observer } from 'mobx-react-lite'

import '../../scss/complex/ratings.scss'
import '../../scss/complex/centers.scss'

const PER_PAGE = 13;

const CentersPage = observer(() => {
    const navigate = useNavigate();
    const {app} = useContext(Context);
    const{lang: lang2} = useParams();

    const [filterOpen, setFilterOpen] = useState(false);
    const [cityOpen, setCityOpen] = useState(false);

    const filterRef = useRef(null);

    useEffect(() => {
        if (!filterOpen) {
            return;
        }

        const handleClickOutside = (event) => {
            if (window.innerWidth <= 767) {
                return;
            }

            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterOpen(false);
                setCityOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [filterOpen]);

    const [name, setName] = useState('');
    const [debouncedName, setDebouncedName] = useState('');
    const [city, setCity] = useState('');

    const [cities, setCities] = useState([]);
    const [allCenters, setAllCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if(app.lang){
            if(app.lang !== lang2){
                navigate(`/${app.lang}/centers`);
            }
        }
    }, [app.lang])

    const {
        items: page,
    } = useFetchItems(AppServices.page, 'centers')

    const translate = (key, fallback) => {
        return app.localizationsItems?.[key] || fallback;
    };

    useEffect(() => {
        const fetchCities = async () => {
            const res = await RegionServices.regions();

            if (res.statusCode === 200) {
                setCities(res.content?.items || []);
            }
        };

        fetchCities();
    }, [app.lang]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedName(name.trim());
        }, 400);

        return () => clearTimeout(timeout);
    }, [name]);

    useEffect(() => {
        const fetchCenters = async () => {
            setLoading(true);

            const query = new URLSearchParams();

            if (debouncedName) {
                query.set('title', debouncedName);
            }

            if (city) {
                query.set('region', city);
            }

            const res = await RegionServices.index(query.toString());

            setAllCenters(res.statusCode === 200 ? res.content?.items || [] : []);
            setCurrentPage(1);
            setLoading(false);
            setInitialLoading(false);
        };

        fetchCenters();
    }, [debouncedName, city, app.lang]);

    const selectedCity = cities.find((item) => item.value === city);

    const pageCount = Math.ceil(allCenters.length / PER_PAGE);

    const centers = allCenters.slice(
        (currentPage - 1) * PER_PAGE,
        currentPage * PER_PAGE
    );

    const params = useMemo(
        () => new URLSearchParams({ page: currentPage }),
        [currentPage]
    );

    const closeFilter = () => {
        setFilterOpen(false);
        setCityOpen(false);
    };

    const handleCitySelect = (value) => {
        setCity(value);
        setCityOpen(false);
    };

    const handleResetFilters = () => {
        setName('');
        setCity('');
        setCityOpen(false);
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected + 1);
    };

  return (
    <>
        <PageTop item={page}/>

        {initialLoading ?
            <div className='page-loader'>
                <span className="loader-circle"></span>
            </div>
        :
        <div className="ratings-container centers-container">
            <div className="ratings-pattern"/>

            <div className="container">
                <div className="article-container-grid ratings-grid">
                    <div className="ratings-content">
                        <h2 className="ratings-title">
                            {translate('centers_title', 'Падел центры')}
                        </h2>

                        <div className="ratings-toolbar centers-toolbar">
                            <div ref={filterRef} className="ratings-filter-wrapper">
                                <button
                                    type="button"
                                    className="ratings-filter-button"
                                    onClick={() => filterOpen ? closeFilter() : setFilterOpen(true)}
                                >
                                    <span className="ratings-filter-button__text">
                                        <span className="material-symbols-outlined ratings-filter-icon">
                                            filter_alt
                                        </span>
                                        <span>
                                            {translate('ratings_filter', 'Фильтр')}
                                        </span>
                                    </span>
                                    <span className={`ratings-filter-arrow ${filterOpen ? 'active' : ''}`}/>
                                </button>

                                {filterOpen && (
                                    <>
                                        <div
                                            className="ratings-filter-overlay"
                                            onClick={closeFilter}
                                        />
                                        <div className="ratings-filter">
                                            <div className="ratings-filter__header">
                                                <span>
                                                    {translate('ratings_filter', 'Фильтр')}
                                                </span>
                                                <button type="button" onClick={closeFilter}>
                                                    ×
                                                </button>
                                            </div>

                                            <div className="ratings-filter__body">
                                                <label>
                                                    <span>
                                                        {translate('centers_name', 'Название')}:
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                    />
                                                </label>

                                                <label>
                                                    <span>
                                                        {translate('centers_city', 'Город')}:
                                                    </span>
                                                    <div className="ratings-select">
                                                        <button
                                                            type="button"
                                                            className="ratings-select__control"
                                                            onClick={() => setCityOpen((prev) => !prev)}
                                                        >
                                                            <span>
                                                                {selectedCity?.title || translate('ratings_all', 'Все')}
                                                            </span>
                                                            <span className={`ratings-select-arrow ${cityOpen ? 'active' : ''}`}/>
                                                        </button>

                                                        {cityOpen && (
                                                            <div className="ratings-select__dropdown">
                                                                <button
                                                                    type="button"
                                                                    className={!city ? 'active' : ''}
                                                                    onClick={() => handleCitySelect('')}
                                                                >
                                                                    {translate('ratings_all', 'Все')}
                                                                </button>
                                                                {cities.map((item) =>
                                                                    <button
                                                                        key={item.id}
                                                                        type="button"
                                                                        className={city === item.value ? 'active' : ''}
                                                                        onClick={() => handleCitySelect(item.value)}
                                                                    >
                                                                        {item.title}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </label>

                                                <button
                                                    type="button"
                                                    className="ratings-filter__reset"
                                                    onClick={handleResetFilters}
                                                >
                                                    {translate('ratings_reset', 'Сбросить всё')}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="ratings-table-wrapper">
                            <table className="ratings-table centers-table">
                                <thead>
                                <tr>
                                    <th>{translate('centers_name', 'Название')}</th>
                                    <th>{translate('centers_address', 'Адрес')}</th>
                                    <th>{translate('centers_courts', 'Количество кортов')}</th>
                                    <th className="centers-table__arrow-col"/>
                                </tr>
                                </thead>
                                {centers.length > 0 && (
                                    <tbody>
                                    {centers.map((item) => (
                                        <tr
                                            key={item.slug}
                                            onClick={() => navigate(`/${app.lang}/centers/${item.slug}`)}
                                        >
                                            <td>
                                                <div className="ratings-player">
                                                    <span className="ratings-player__name">
                                                        {item.title}
                                                    </span>
                                                    {item.time &&
                                                        <span className="ratings-player__date">
                                                            {item.time}
                                                        </span>
                                                    }
                                                </div>
                                            </td>
                                            <td>
                                                {item.region &&
                                                    <strong className="centers-table__city">
                                                        {item.region}{item.address ? ',' : ''}
                                                    </strong>
                                                }
                                                {item.address &&
                                                    <span className="centers-table__address">
                                                        {item.address}
                                                    </span>
                                                }
                                            </td>
                                            <td>{item.court_count ?? '—'}</td>
                                            <td>
                                                <NavLink
                                                    to={`/${app.lang}/centers/${item.slug}`}
                                                    className="centers-table__link"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span className="material-symbols-outlined">
                                                        arrow_forward
                                                    </span>
                                                </NavLink>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                )}
                            </table>

                            {!loading && !centers.length && (
                                <div className="ratings-empty">
                                    {app.localizationsItems?.no_data}
                                </div>
                            )}

                            {loading && (
                                <div className="ratings-table-loader">
                                    <span className="loader-circle"/>
                                </div>
                            )}
                        </div>

                        {pageCount > 1 && (
                            <div className="custome-pagination">
                                <Pagination
                                    loading={loading}
                                    pageCount={pageCount}
                                    params={params}
                                    handlePageClick={handlePageClick}
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <LatestArticles gray={true}/>
                    </div>
                </div>
            </div>
        </div>
        }
    </>
  )
})

export default CentersPage
